import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const WaterFootprint = () => {
  // State variables for Water Footprint Calculation
  const [stages, setStages] = useState([{ stage: "", waterUsed: 0 }]); // Array of production stages and water usage
  const [waterFootprint, setWaterFootprint] = useState(0); // Calculated water footprint
  const [waterUnit, setWaterUnit] = useState("liters"); // Unit for water used (liters, gallons, cubic meters)

  // Event handler to change the water unit
  const handleWaterUnitChange = (e) => setWaterUnit(e.target.value);

  // Event handler to update water used in each stage
  const handleStageChange = (index, value) => {
    const updatedStages = [...stages];
    updatedStages[index].waterUsed = parseFloat(value) || 0;
    setStages(updatedStages);
  };

  // Event handler to update stage name
  const handleStageNameChange = (index, value) => {
    const updatedStages = [...stages];
    updatedStages[index].stage = value;
    setStages(updatedStages);
  };

  // Event handler to add a new production stage
  const handleAddStage = () => {
    setStages([...stages, { stage: "", waterUsed: 0 }]);
  };

  // Event handler to remove a production stage
  const handleRemoveStage = (index) => {
    const updatedStages = stages.filter((_, i) => i !== index);
    setStages(updatedStages);
  };

  // useEffect hook to calculate total water footprint when stages change
  useEffect(() => {
    const totalWaterUsed = stages.reduce(
      (acc, stage) => acc + stage.waterUsed,
      0
    );
    setWaterFootprint(totalWaterUsed);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [stages]);

  return (
    <Layout
      title="Water Footprint Calculator"
      path="water-footprint-calculator"
    >
      <section className="section-padding" id="water-footprint-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Water Footprint Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Water Footprint Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Stage Inputs */}
                    {stages.map((stage, index) => (
                      <div className="col-md-6 col-lg-4" key={index}>
                        <label
                          htmlFor={`stage-${index}`}
                          className="form-label"
                        >
                          Production Stage {index + 1}
                          <span
                            className="ms-2 text-muted"
                            data-bs-toggle="popover"
                            data-bs-html="true"
                            data-bs-placement="top"
                            data-bs-content="<strong>Production Stage:</strong> Enter the name of the stage in the production process and the water used during that stage."
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bi bi-info-circle"></i>
                          </span>
                        </label>
                        <div className="d-flex">
                          <input
                            type="text"
                            className="form-control"
                            id={`stage-${index}`}
                            placeholder="Enter stage name"
                            value={stage.stage}
                            onChange={(e) =>
                              handleStageNameChange(index, e.target.value)
                            }
                          />
                          <input
                            type="number"
                            className="form-control ms-2"
                            placeholder="Water used"
                            value={stage.waterUsed || ""}
                            onChange={(e) =>
                              handleStageChange(index, e.target.value)
                            }
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-danger mt-2"
                          onClick={() => handleRemoveStage(index)}
                        >
                          <i class="fa-solid fa-minus"></i>
                        </button>
                      </div>
                    ))}

                    {/* Button to Add More Stages */}
                    <div className="col-md-12">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddStage}
                      >
                        <i class="fa-solid fa-plus"></i>
                      </button>
                    </div>
                  </div>

                  {/* Water Unit Selection */}
                  <div className="mt-4">
                    <div className="form-group">
                      <label htmlFor="waterUnit" className="form-label">
                        Select Water Unit
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Water Unit:</strong> Choose the unit of measurement for the water used (liters, gallons, or cubic meters)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <select
                        className="form-select"
                        id="waterUnit"
                        value={waterUnit}
                        onChange={handleWaterUnitChange}
                      >
                        <option value="liters">Liters</option>
                        <option value="gallons">Gallons</option>
                        <option value="cubic meters">Cubic Meters</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Water Footprint:{" "}
                        <span className="fw-bold ms-3">
                          {waterFootprint === 0
                            ? "0.00"
                            : waterFootprint.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          {` ${waterUnit}`}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Water Footprint Calculator
                </h3>

                {/* What is a Water Footprint Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Water Footprint Calculator?
                </h4>
                <p>
                  The **Water Footprint Calculator** helps to estimate the total
                  water used in the production of a product or running a
                  business across multiple stages. It sums the water consumed at
                  each stage, providing a total water usage figure that can
                  inform sustainable practices.
                </p>

                {/* Why Use a Water Footprint Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Water Footprint Calculator?
                </h4>
                <p>
                  Understanding the water footprint of a product or process can
                  help reduce unnecessary water consumption, improve
                  sustainability efforts, and guide resource management in a
                  more environmentally-friendly direction.
                </p>

                {/* Benefits of Using a Water Footprint Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Water Footprint Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Supports sustainable water management:</strong>{" "}
                    Helps track and reduce water consumption across different
                    stages of production.
                  </li>
                  <li>
                    <strong>
                      Identifies opportunities to reduce water usage:
                    </strong>{" "}
                    Pinpoints areas where water-saving measures can be
                    implemented.
                  </li>
                  <li>
                    <strong>Useful for environmental reporting:</strong>{" "}
                    Provides accurate data for businesses aiming to report or
                    improve environmental impacts.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Water Footprint Calculator?
                </h4>
                <p>The Water Footprint Calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Manufacturing industries:</strong> To assess water
                    use at each stage of production and minimize water waste.
                  </li>
                  <li>
                    <strong>Agriculture:</strong> To estimate water consumption
                    in farming processes and improve irrigation efficiency.
                  </li>
                  <li>
                    <strong>Sustainability efforts:</strong> To track and report
                    environmental impact and adopt water-saving strategies.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How Can a Water Footprint Calculator Help With Sustainability
                  Initiatives?
                </h4>
                <p>
                  By calculating water usage across various stages, businesses
                  can identify opportunities to reduce water waste, implement
                  more efficient practices, and achieve their sustainability
                  goals.
                </p>

                <h4 className="mt-4 mb-3">
                  Can a Water Footprint Calculator Be Used for Both Small and
                  Large Projects?
                </h4>
                <p>
                  Yes, the Water Footprint Calculator is versatile enough to be
                  used for both small-scale and large-scale operations, offering
                  a clear understanding of water usage no matter the size of the
                  project.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WaterFootprint;
